import {CustomFieldDefinition, Record, ReportRecord, RunningEntry} from "@/shared/timr";
import {api, RequestParam} from "@/shared/api";
import {getCurrentISOOffset} from "@/shared/utils/datefunctions";
import {
	RecordingStatus,
	RecordingStatusPanel
} from "@/shared/recording/recordingStatusPanel";
import {RecordingTable, RecordingDataTableResponse} from "@/shared/recording/recordingTable";
import {ValidationRecordEventDetail, ValidationsCollapseComponent} from "@/features/validations/validationsCollapseComponent";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {RecordDetailsDialog} from "@/shared/recording/recordDetailsDialog";
import * as Button from "@/shared/utils/button";
import {DateTimeChangeableByUserDayRangeMode} from "@/shared/timrTypes";

export interface BaseRecordingConfig<TStatus extends RecordingStatus> {
	currentStatus: TStatus;
	statusNumbers: number[];
	defaultStatusNumber: number;
	customFieldDefinitions: CustomFieldDefinition[];
	allowedUserIds: number[];
	defaultUserId: number;
	isGroupLeaderForThemself: boolean;
	dateTimeChangeableByUserDayRangeUntil: string | null;
	dateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
}

export abstract class BaseRecording<
	T extends ReportRecord,
	TStatus extends RecordingStatus,
	TLastRecording extends Record
> {

	private static preventSessionTimeoutInterval = 15 * 60 * 1000;
	protected static RECORDING_TABLE_PAGE_LENGTH = 15;

	protected statusPanel: RecordingStatusPanel<TStatus, RunningEntry, TLastRecording>;
	protected recordingTable: RecordingTable<T>;
	protected recordingDialog: RecordDialog<T>;
	protected recordingDetailsDialog: RecordDetailsDialog<T>;
	protected validationsCollapseComponent: ValidationsCollapseComponent;

	private statisticsStatusRequest: JQueryXHR;

	constructor(public config: BaseRecordingConfig<TStatus>) {
		this.init();
		this.statusPanel.updateStatus(config.currentStatus);
	}

	protected init() {
		this.statusPanel = this.createStatusPanel();
		$(this.statusPanel).on(RecordingStatusPanel.EVENT_RECORDING_CHANGED, () => {
			this.reloadTable();
		}).on(RecordingStatusPanel.EVENT_LAST_RECORDING_ID_CHANGED, (e, lastRecordingId: number) => {
			this.recordingTable.setLastRecordingId(lastRecordingId);
		}).on(RecordingStatusPanel.EVENT_STATUS_UPDATED, (e, data: TStatus) => {
			this.updateStatistics(data);
		}).on(RecordingStatusPanel.EVENT_BUTTON_UPDATED, (e, runningRecording: boolean) => {
			this.recordingTable.setCurrentRunningRecording(runningRecording);
			this.recordingTable.updateQuickSwitch();
		});

		this.recordingTable = this.createRecordingTable();
		$(this.recordingTable).on(RecordingTable.EVENT_DATA_CHANGED, () => {
			this.statusPanel.fetchRecordingStatus();
			this.refreshStatistics();
		}).on(RecordingTable.EVENT_EDIT_RECORDING, (e, recording: T) => {
			this.editRecording(recording);
		}).on(RecordingTable.EVENT_SHOW_RECORDING, (e, recording: T) => {
			this.showRecording(recording);
		}).on(RecordingTable.EVENT_DUPLICATE_RECORDING, (e, recording: T) => {
			this.duplicateRecording(recording);
		}).on(RecordingTable.EVENT_RESUME_RECORDING, (e, recording: T) => {
			this.resumeRecording(recording);
		}).on(RecordingTable.EVENT_QUICK_SWITCH_RECORDING, (e, recordingId: number) => {
			this.quickSwitchRecording(recordingId);
		});

		this.recordingDialog = this.createRecordingDialog();
		$(this.recordingDialog).on(RecordDialog.EVENT_RECORD_EDITED, () => {
			this.reloadTable();
		}).on(RecordDialog.EVENT_RECORD_CREATED, () => {
			this.reloadTable();
		}).on(RecordDialog.EVENT_RECORD_DELETED, () => {
			this.reloadTable();
		});

		this.checkFocus();

		this.preventSessionTimeout();
	}

	protected abstract createStatusPanel(): RecordingStatusPanel<TStatus, RunningEntry, TLastRecording>;

	protected abstract createRecordingTable(): RecordingTable<T>;

	protected abstract createRecordingDialog(): RecordDialog<T>;

	protected abstract createRecordingDetailsDialog(): RecordDetailsDialog<T>;

	protected abstract addRecording(lastRecording: TLastRecording | null): void;

	protected abstract showRecording(recording: T): void;

	protected abstract resumeRecording(recording: T): void;

	protected abstract isDateTimeChangeAllowed(): boolean;

	protected quickSwitchRecording(recordingId: number) {
		this.statusPanel.shouldQuickSwitchRecording(recordingId, () => {
			Button.setLoading(this.recordingTable.config.element.find("button.quickSwitchButton, button.quickStartButton"), "", true);
		}, () => {
			Button.resetLoading(this.recordingTable.config.element.find("button.quickSwitchButton, button.quickStartButton"));
		});
	}

	protected abstract updateStatistics(data: TStatus): void;

	private refreshStatistics(): void {
		this.statisticsStatusRequest?.abort();
		this.statisticsStatusRequest = api(this.statusPanel.getFetchStatusURL())
			.get({localTimeZone: getCurrentISOOffset()})
			.done((status: TStatus) => this.updateStatistics(status));
	}

	protected editRecording(recording: T) {
		this.recordingDialog.edit(recording);
	}

	protected duplicateRecording(recording: T) {
		this.recordingDialog.duplicate(recording);
	}

	protected buildFilterParams(): RequestParam[] {
		return [{name: "id", value: this.config.defaultUserId}];
	}

	protected reloadTable() {
		return this.recordingTable.filter(this.buildFilterParams());
	}

	protected abstract getRecordById(recordId: number, link: JQuery): void;

	protected initValidationsListeners() {
		if (globals.savedValidationsEnabled) {
			$(this.recordingTable).on(RecordingTable.EVENT_DATA_CHANGED, (e, data: RecordingDataTableResponse<T>) => {
				const relevantValidations = data?.validations?.relevant;
				if (relevantValidations) {
					this.recordingTable.updateSeverityHeaderIcon(relevantValidations);

					// update data of validations collapse
					this.validationsCollapseComponent.content = {
						validations: relevantValidations,
						// DEVNOTE isAddWorkingTimeButtonActive/isAddAbsenceButtonActive are only relevant for validations which contain an "add wt" context menu
						// These are currently only displayed in the time account/validations page, so they are set to false here
						isAddWorkingTimeButtonActive: false,
						isAddAbsenceButtonActive: false,
						validationTotalCount: relevantValidations.length
					};
				}
			});

			// init validations web component and add type
			this.validationsCollapseComponent = document.getElementById("validationsCollapse") as ValidationsCollapseComponent;

			// handle click events on referenced records of validations
			this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_CLICKED, e => {
				const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
				this.getRecordById(customEvent.detail.id, customEvent.detail.link);
			});
			this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_REF_CLICKED, e => {
				const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
				this.getRecordById(customEvent.detail.id, customEvent.detail.link);
			});

			this.recordingTable.getDataTable().on("processing.dt", (e, s, processing: boolean) => {
				// set collapse loading on table reload
				this.validationsCollapseComponent.toggleLoadingOverlay(processing);
			});
		}
	}

	private checkFocus() {
		$(window).focus(() => {
			this.statusPanel.activateUpdater();
		}).blur(() => {
			this.statusPanel.deactivateUpdater();
		});
	}

	private preventSessionTimeout() {
		setInterval(() => {
			this.statusPanel.fetchRecordingStatus(true, true);
		}, BaseRecording.preventSessionTimeoutInterval);
	}
}
